/**
 * A persisted workouts block, read onto the workouts family's own model.
 *
 * The mapping lives here rather than inside the card for the reason every
 * family keeps its decisions in a model module: it is pure, it is where the
 * honesty rules are enforced, and it can be tested against a block the backend
 * projector actually wrote without mounting React.
 *
 * WHAT A BLOCK CAN AND CANNOT PROVE. A live search hands the family a catalog
 * record with the vendor's own handle, three shoot booleans and a premium flag.
 * A block carries none of those, and none is invented here: the shoot flags are
 * `false` as a FACT ABOUT THE BLOCK rather than a claim about the movement —
 * the same reading the family already applies to a plan slot, whose projection
 * of the same movement fills no catalog flag either — and `isPremium` is null,
 * which is the shape's own word for "nobody said".
 *
 * THE VIDEO IS THE POINT. `hasVideo` is exactly whether the block printed a
 * `video:` line, because the projector prints one if and only if the catalog
 * published a demonstration; there is no third state to represent. The address
 * is DURABLE — a `/media/exercise-video/<handle>` route that resolves a live URL
 * at click time — so the media it builds carries NO `capturedAt` and NO
 * `expiresAt`: nothing was signed, so nothing can expire, and the family's state
 * machine reads that as `Playable` for as long as the block exists.
 *
 * IDENTITY. A block-read row has no vendor id. Its handle is recovered from the
 * demonstration address, which is the one place a block carries it, and its card
 * key is scoped to the block it came out of — the only scope a persisted item
 * has — so two rows of one block are distinct by position by construction.
 */
import {
  AttachResultShape,
  WorkoutsAttachPhase,
  WorkoutsAttachRecordKind,
  type AttachResult,
  type ParsedAttachResults,
  type WorkoutsAttachExercise,
  type WorkoutsAttachSlot,
  type WorkoutsAttachWorkout,
} from "@cabal/shared/domain/attached-result/index.js";
/**
 * The family's vocabulary comes from its types LEAF rather than its barrel: the
 * barrel re-exports components that import a stylesheet, and this module's own
 * test runs under `node --import tsx --test`, which has no CSS loader.
 */
import {
  CworkoutcardPhase,
  type CworkoutcardData,
  type CworkoutcardMedia,
  type CworkoutcardPlanData,
  type CworkoutcardPlanSlot,
  type CworkoutcardPrescription,
} from "@/components/ui/Cworkoutcard/cworkoutcard.types";

/** What separates a row's position from its own name in a card key. */
const KEY_SEPARATOR = ":";
/** The one clip a block can describe, and its own key inside the row. */
const CLIP_KEY = "demonstration";
/** How a demonstration address ends: the handle the catalog files it under. */
const ADDRESS_SEGMENT_SEPARATOR = "/";

/** Every workouts row a block holds, split by the record it is. */
export interface CattachedresultWorkoutsModel {
  readonly exercises: readonly CworkoutcardData[];
  readonly plans: readonly CworkoutcardPlanData[];
}

/** The three phase names, one table, so the two vocabularies cannot drift. */
const PHASE_BY_ATTACH_PHASE: Record<WorkoutsAttachPhase, CworkoutcardPhase> = {
  [WorkoutsAttachPhase.Warmup]: CworkoutcardPhase.Warmup,
  [WorkoutsAttachPhase.Main]: CworkoutcardPhase.Main,
  [WorkoutsAttachPhase.Cooldown]: CworkoutcardPhase.Cooldown,
};

/**
 * The catalog handle inside a demonstration address, or null.
 *
 * The address is composed as `/media/exercise-video/<handle>` by one function
 * on the backend, so its last segment is the handle and nothing else. A value
 * that is not a URL at all answers null rather than being sliced on a guess.
 */
export function cattachedresultWorkoutHandle(
  videoUrl: string | null,
): string | null {
  if (videoUrl == null) return null;
  const path = videoUrl.split(ADDRESS_SEGMENT_SEPARATOR);
  const handle = path[path.length - 1];
  if (handle == null || handle.length === 0) return null;
  return decodeURIComponent(handle);
}

/** The row's identity inside its own block. */
function keyOf(input: { index: number; name: string }): string {
  return `${String(input.index)}${KEY_SEPARATOR}${input.name}`;
}

/**
 * The demonstration, where the block printed an address for one.
 *
 * `durationSecs` is the block's own figure and is null on every captured
 * record, so the family's duration read stays null and no surface prints a
 * zero.
 */
function mediaOf(input: {
  result: WorkoutsAttachExercise;
}): CworkoutcardMedia | null {
  const videoUrl = input.result.videoUrl;
  if (videoUrl == null) return null;
  return {
    clips: [
      {
        key: CLIP_KEY,
        videoUrl,
        // A block carries the one durable address and no playlist beside it.
        videoHlsUrl: null,
        // No poster is published for this address: the frame states the
        // movement's name and arms the player against the address itself.
        poster: null,
        shoot: null,
        orientation: null,
        primary: true,
      },
    ],
    durationSecs: input.result.videoSeconds,
    capturedAt: null,
    expiresAt: null,
  };
}

function exerciseOf(input: {
  result: WorkoutsAttachExercise;
  index: number;
}): CworkoutcardData {
  const result = input.result;
  return {
    key: keyOf({ index: input.index, name: result.name }),
    slug: cattachedresultWorkoutHandle(result.videoUrl),
    title: result.name,
    description: result.description ?? "",
    instructions: result.steps,
    importantPoints: result.formPoints,
    muscleGroup: result.muscle ?? "",
    secondaryMuscles: result.secondaryMuscles,
    equipment: result.equipment ?? "",
    category: result.category,
    difficulty: result.difficulty ?? "",
    exerciseTypes: result.trainingTypes,
    hasVideo: result.videoUrl != null,
    // Facts about the block, not claims about the movement: a block states
    // which demonstration it can open, never which shoots exist.
    hasWhiteBackgroundVideo: false,
    hasGymShotVideo: false,
    isPremium: null,
    media: mediaOf({ result }),
    prescription: null,
  };
}

/** A slot's prescription, only where the block stated all three figures. A
 *  partial prescription is not a prescription — the family draws none rather
 *  than a line with a hole in it. */
function prescriptionOf(input: {
  slot: WorkoutsAttachSlot;
  order: number;
}): CworkoutcardPrescription | null {
  const slot = input.slot;
  if (slot.sets == null || slot.repetitions == null || slot.restSeconds == null) {
    return null;
  }
  return {
    order: input.order,
    sets: slot.sets,
    repetitions: slot.repetitions,
    restSeconds: slot.restSeconds,
  };
}

/**
 * One slot as the family's own pair.
 *
 * A slot names a movement and prescribes it; it does not describe it, so every
 * descriptive field is empty here and the plan draws the title, the muscle it
 * has and the prescription. The phase is the block's own where it named one,
 * and the main block otherwise — which is where a generator that named no phase
 * put the movement.
 */
function planSlotOf(input: {
  slot: WorkoutsAttachSlot;
  index: number;
}): CworkoutcardPlanSlot {
  const slot = input.slot;
  const order = input.index + 1;
  return {
    phase:
      slot.phase == null
        ? CworkoutcardPhase.Main
        : PHASE_BY_ATTACH_PHASE[slot.phase],
    exercise: {
      key: keyOf({ index: input.index, name: slot.name }),
      slug: cattachedresultWorkoutHandle(slot.videoUrl),
      title: slot.name,
      description: "",
      instructions: [],
      importantPoints: [],
      muscleGroup: "",
      secondaryMuscles: [],
      equipment: "",
      category: null,
      difficulty: "",
      exerciseTypes: [],
      hasVideo: slot.videoUrl != null,
      hasWhiteBackgroundVideo: false,
      hasGymShotVideo: false,
      isPremium: null,
      media:
        slot.videoUrl == null
          ? null
          : {
              clips: [
                {
                  key: CLIP_KEY,
                  videoUrl: slot.videoUrl,
                  videoHlsUrl: null,
                  poster: null,
                  shoot: null,
                  orientation: null,
                  primary: true,
                },
              ],
              durationSecs: null,
              capturedAt: null,
              expiresAt: null,
            },
      prescription: prescriptionOf({ slot, order }),
    },
  };
}

function planOf(input: {
  result: WorkoutsAttachWorkout;
  index: number;
}): CworkoutcardPlanData {
  const result = input.result;
  return {
    key: keyOf({ index: input.index, name: result.name }),
    name: result.name,
    difficulty: result.difficulty,
    estimatedMinutes: result.minutes,
    muscleGroups: result.muscles,
    slots: result.slots.map((slot, index) => planSlotOf({ slot, index })),
  };
}

function isWorkoutsResult(
  result: AttachResult,
): result is AttachResult & { readonly shape: AttachResultShape.Workouts } {
  return result.shape === AttachResultShape.Workouts;
}

/**
 * Every workouts row of one parsed block, on the family's model.
 *
 * A block whose items did not read as this pillar's shape yields nothing at
 * all: the shared reader has already degraded it to the generic arm, and the
 * card falls back to the lossless render rather than drawing an empty set.
 */
export function cattachedresultWorkouts(input: {
  block: ParsedAttachResults;
}): CattachedresultWorkoutsModel {
  const results = input.block.results.filter(isWorkoutsResult);
  const exercises = results.flatMap((result, index) =>
    result.record === WorkoutsAttachRecordKind.Exercise
      ? [exerciseOf({ result, index })]
      : [],
  );
  const plans = results.flatMap((result, index) =>
    result.record === WorkoutsAttachRecordKind.Workout
      ? [planOf({ result, index })]
      : [],
  );
  return { exercises, plans };
}
